/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import {
  ExternalStyles,
  focusBoxShadow,
  Icon as IconBold,
  merge,
  StepProps,
  StepStatus,
  Theme,
  useTheme,
} from 'bold-ui'
import { useMemo } from 'react'

interface VerticalStepProps {
  status?: StepStatus
  connectorStatus?: StepStatus
  hasConnector?: boolean
  overrides?: StepProps['overrides']
  header: React.ReactNode
  body?: React.ReactNode
  style?: ExternalStyles
  headerStyle?: ExternalStyles
  connectorStyle?: ExternalStyles
}

export function VerticalStep(props: VerticalStepProps) {
  const {
    hasConnector = true,
    status = 'incompleted',
    connectorStatus,
    overrides,
    header,
    body,
    style,
    headerStyle,
    connectorStyle,
    ...rest
  } = props
  const { Root, Icon } = getComponents(overrides, defaultComponents)

  const computedConnectorStatus = connectorStatus ?? getConnectorStatus(status)

  const theme = useTheme()
  const classes = useMemo(() => createStyles(theme, status, computedConnectorStatus), [
    computedConnectorStatus,
    status,
    theme,
  ])

  return (
    <Root css={css(classes.step, style)} {...rest}>
      {hasConnector && <span css={css(classes.connector, connectorStyle)} />}

      <div css={css(classes.stepHeader, headerStyle)}>
        <span css={classes.stepContainer}>
          {Icon && <Icon className={classes.icon} />}
          {!Icon && (
            <span css={classes.iconContainer}>
              {status === 'completed' && <IconBold icon='checkDefault' css={classes.icon} />}
            </span>
          )}
        </span>

        <span css={classes.stepLabel}>{header}</span>
      </div>
      {body && <div css={classes.stepBody}>{body}</div>}
    </Root>
  )
}

const defaultComponents: StepProps['overrides'] = {
  Root: 'span',
  Icon: null,
}

function getComponents<T>(overrides: T, defaultComponents: T) {
  return merge<T, T, T>({} as any, defaultComponents, overrides)
}

function getConnectorStatus(stepStatus: StepProps['status']): StepProps['status'] {
  if (stepStatus === 'active' || stepStatus === 'incompleted') {
    return 'incompleted'
  }
  return 'completed'
}

const createStyles = (theme: Theme, status: StepProps['status'], computedConnectorStatus: StepProps['status']) => ({
  step: css`
    position: relative;
  `,
  stepHeader: css`
    min-height: 2.5rem;
    height: 2.5rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;
    text-align: left;
  `,
  stepBody: css`
    margin-left: 2.25rem;
  `,
  connector: css`
    height: calc(100% - 1rem);
    position: absolute;
    top: 1.75rem;
    left: 0.688rem;
    right: 0;
    border-left-width: 0.125rem;
    border-left-style: solid;
    border-left-color: ${computedConnectorStatus === 'incompleted'
      ? theme.pallete.gray.c80
      : theme.pallete.primary.main};
    transition: all 0.4s ease;
  `,
  stepContainer: css`
    width: 1.5rem;
    min-width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  iconContainer: css`
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    min-width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: ${status === 'incompleted' ? theme.pallete.gray.c80 : theme.pallete.primary.main};
    text-align: center;
    transition: all 0.4s ease;
    box-shadow: ${(status === 'active' && focusBoxShadow(theme, 'primary')) ||
    (status === 'completed' && `0 0 0 4px ${theme.pallete.primary.main}`)};
  `,
  icon: css`
    fill: ${theme.pallete.primary.c100};
    width: 1rem;
    height: 1rem;
  `,
  stepLabel: css`
    color: ${status === 'active' && theme.pallete.primary.main};
    font-weight: bold;
    margin-left: 0.75rem;
    transition: all 0.4s ease;
  `,
})
